import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { MdSearch, MdEdit, MdDelete, MdError, MdFilterList, MdCalendarToday, MdScale, MdCategory, MdClose, MdVisibility, MdQrCode2, MdDownload, MdPrint, MdFileDownload } from "react-icons/md";
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, recordId, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-navy-800">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-navy-700 dark:text-white">Confirm Deletion</h3>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MdClose className="h-6 w-6" />
          </button>
        </div>
        <p className="mb-6 text-gray-600 dark:text-gray-300">
          Are you sure you want to delete this record? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const CalibrationRecords = () => {
  const TABLE_HEAD = ["ID", "Date", "Type", "Origin", "Size", "Quantity", "Bags", "Outturn", "Moisture", "Zone", "Agent", "Action"];
  const [calibrationData, setCalibrationData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    size: '',
    origin: '',
    type: '' // RCN type (Organic, Fair Trade, Conventional)
  });
  const itemsPerPage = 10;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [downloadingQrId, setDownloadingQrId] = useState(null);
  const [downloadingCardId, setDownloadingCardId] = useState(null);
  const [printingCardId, setPrintingCardId] = useState(null);

  // Fetch data from API
  useEffect(() => {
    const fetchCalibrationData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://usibras-web-backend.onrender.com/api/fetch-calibration-data');
        console.log('API Response:', response.data);
        
        if (response.data && response.data.data) {
          // Access the data array from the response
          const data = Array.isArray(response.data.data) ? response.data.data : [];
          console.log('Processed Data:', data);
          setCalibrationData(data);
        } else {
          setCalibrationData([]);
          setError('Failed to fetch data');
        }
      } catch (err) {
        console.error('API Error:', err);
        setCalibrationData([]);
        setError('Error connecting to the server');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCalibrationData();
  }, []);

  // Calculate summary statistics
  const calculateSummary = (data) => {
    console.log('Calculating summary for data:', data);
    if (!Array.isArray(data)) return {
      totalCalibrations: 0,
      totalWeight: '0.00',
      totalBags: 0,
      avgOutturn: '0.00'
    };

    const totalCalibrations = data.length;
    const totalWeight = data.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
    const totalBags = data.reduce((sum, item) => sum + (parseInt(item.number_of_bags) || 0), 0);
    const avgOutturn = data.reduce((sum, item) => sum + (parseFloat(item.outturn) || 0), 0) / (data.length || 1);

    const summary = {
      totalCalibrations,
      totalWeight: new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(totalWeight),
      totalBags,
      avgOutturn: avgOutturn.toFixed(2)
    };
    
    console.log('Calculated summary:', summary);
    return summary;
  };

  // Get unique values for filters
  const uniqueSizes = Array.isArray(calibrationData) ? [...new Set(calibrationData.map(item => item.size))] : [];
  const uniqueOrigins = Array.isArray(calibrationData) ? [...new Set(calibrationData.map(item => item.origin))] : [];
  const uniqueTypes = Array.isArray(calibrationData) ? [...new Set(calibrationData.map(item => item.rcn_type))] : [];

  console.log('Unique values:', { uniqueSizes, uniqueOrigins, uniqueTypes });

  // Apply filters
  const filteredData = Array.isArray(calibrationData) ? calibrationData.filter(item => {
    const matchesSearch = 
      (item.origin && item.origin.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.size && item.size.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.rcn_type && item.rcn_type.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesDate = (!filters.startDate || new Date(item.created_at) >= new Date(filters.startDate)) &&
                       (!filters.endDate || new Date(item.created_at) <= new Date(filters.endDate));
    
    const matchesSize = !filters.size || item.size === filters.size;
    const matchesOrigin = !filters.origin || item.origin === filters.origin;
    const matchesType = !filters.type || item.rcn_type === filters.type;

    return matchesSearch && matchesDate && matchesSize && matchesOrigin && matchesType;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : [];

  console.log('Filtered Data:', filteredData);

  const summary = calculateSummary(filteredData);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleEdit = (id) => {
    // Implement edit functionality
    console.log(`Edit item with ID: ${id}`);
  };

  const handleDelete = (id) => {
    setRecordToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleExportCsv = () => {
    if (!filteredData.length) {
      toast.error('No data to export');
      return;
    }
    const headers = ['ID', 'Date', 'Type', 'Origin', 'Size', 'Quantity (kg)', 'Bags', 'Outturn', 'Moisture (%)', 'Zone', 'Agent'];
    const rows = filteredData.map(item => [
      item.id,
      item.created_at ? new Date(item.created_at).toLocaleDateString() : '',
      item.rcn_type ?? '',
      item.origin ?? '',
      item.size ?? '',
      item.real ?? '',
      item.number_of_bags ?? '',
      item.outturn ?? '',
      item.moisture ?? '',
      item.zone ?? '',
      item.agent ?? '',
    ]);
    const escape = (val) => {
      const str = String(val ?? '');
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };
    const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calibration-records-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredData.length} records to CSV`);
  };

  const navigate = useNavigate();

  const loadImageFromObjectUrl = (objectUrl) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load image'));
    img.src = objectUrl;
  });

  const handleDownloadQr = async (item) => {
    try {
      setDownloadingQrId(item.id);
      const recordUrl = `${window.location.origin}/admin/warehouse/calibrated/${item.id}`;
      const qrApi = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(recordUrl)}`;
      const res = await fetch(qrApi);
      if (!res.ok) throw new Error('QR generation failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `calibration-${item.id}-qrcode.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success('QR code downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to download QR code');
    } finally {
      setDownloadingQrId(null);
    }
  };

  const handleDownloadRecordCard = async (item, printImmediately = false) => {
    try {
      if (printImmediately) {
        setPrintingCardId(item.id);
      } else {
        setDownloadingCardId(item.id);
      }

      const recordUrl = `${window.location.origin}/admin/warehouse/calibrated/${item.id}`;
      const qrApi = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(recordUrl)}`;
      const qrRes = await fetch(qrApi);
      if (!qrRes.ok) throw new Error('QR generation failed');

      const qrBlob = await qrRes.blob();
      const qrObjectUrl = URL.createObjectURL(qrBlob);

      const canvas = document.createElement('canvas');
      const width = 1600;
      const height = 900;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) throw new Error('Canvas not supported in this browser');

      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#f8fafc');
      gradient.addColorStop(1, '#e2e8f0');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      const cardX = 70;
      const cardY = 70;
      const cardW = width - 140;
      const cardH = height - 140;

      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.roundRect(cardX, cardY, cardW, cardH, 28);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#0f172a';
      ctx.font = '700 56px "Arial"';
      ctx.fillText('Warehouse Calibration Record', cardX + 55, cardY + 95);

      ctx.fillStyle = '#334155';
      ctx.font = '600 30px "Arial"';
      ctx.fillText(`Record ID: ${item.id}`, cardX + 55, cardY + 145);

      const details = [
        ['Date', item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'],
        ['Type', item.rcn_type || '-'],
        ['Origin', item.origin || '-'],
        ['Size', item.size || '-'],
        ['Quantity', `${item.real || 0} kg`],
        ['Bags', `${item.number_of_bags || 0}`],
        ['Outturn', `${item.outturn || 0}`],
        ['Moisture', `${item.moisture || 0}%`],
        ['Zone', item.zone || '-'],
        ['Agent', item.agent || '-']
      ];

      const leftColX = cardX + 55;
      const rightColX = cardX + 540;
      const firstRowY = cardY + 230;
      const rowGap = 78;

      details.forEach(([label, value], index) => {
        const isLeft = index < 5;
        const x = isLeft ? leftColX : rightColX;
        const y = firstRowY + (isLeft ? index : index - 5) * rowGap;

        ctx.fillStyle = '#64748b';
        ctx.font = '600 28px "Arial"';
        ctx.fillText(`${label}:`, x, y);

        ctx.fillStyle = '#0f172a';
        ctx.font = '700 34px "Arial"';
        ctx.fillText(`${value}`, x, y + 42);
      });

      const qrImage = await loadImageFromObjectUrl(qrObjectUrl);
      URL.revokeObjectURL(qrObjectUrl);

      const qrSize = 420;
      const qrX = cardX + cardW - qrSize - 70;
      const qrY = cardY + 130;

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.roundRect(qrX - 18, qrY - 18, qrSize + 36, qrSize + 36, 20);
      ctx.fill();

      ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

      ctx.fillStyle = '#1e293b';
      ctx.font = '700 26px "Arial"';
      ctx.fillText('Scan to view full record', qrX - 5, qrY + qrSize + 55);

      const footerText = 'USIBRAS Warehouse Calibration';
      ctx.fillStyle = '#475569';
      ctx.font = '600 24px "Arial"';
      ctx.fillText(footerText, cardX + 55, cardY + cardH - 40);

      const imageDataUrl = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [width, height]
      });
      pdf.addImage(imageDataUrl, 'PNG', 0, 0, width, height);

      if (printImmediately) {
        pdf.autoPrint();
        const blobUrl = pdf.output('bloburl');
        const printWindow = window.open(blobUrl, '_blank');
        if (!printWindow) {
          toast.error('Pop-up blocked. Allow pop-ups to print automatically.');
        } else {
          toast.success('Print dialog opened');
        }
      } else {
        pdf.save(`calibration-${item.id}-record.pdf`);
        toast.success('Record PDF downloaded');
      }
    } catch (err) {
      console.error(err);
      toast.error(printImmediately ? 'Failed to print record PDF' : 'Failed to download record PDF');
    } finally {
      setDownloadingCardId(null);
      setPrintingCardId(null);
    }
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await axios.delete(`https://usibras-web-backend.onrender.com/delete_calibration_record/${recordToDelete}`);
      if (response.status === 200) {
        toast.success("Record deleted successfully");
        setCalibrationData(calibrationData.filter(item => item.id !== recordToDelete));
      }
    } catch (err) {
      console.error('Delete Error:', err);
      toast.error('Error deleting record. Please try again.');
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setRecordToDelete(null);
    }
  };

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-4"}>
        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
                <MdCategory className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Calibrations</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.totalCalibrations}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-green-50 p-3 dark:bg-navy-700">
                <MdScale className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Weight</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.totalWeight} kg</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-purple-50 p-3 dark:bg-navy-700">
                <MdCategory className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Bags</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.totalBags}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-orange-50 p-3 dark:bg-navy-700">
                <MdScale className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Outturn</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.avgOutturn}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters Section */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
          <div className="mb-4 flex items-center gap-2">
            <MdFilterList className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-white">Filters</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Date Range</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                />
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                />
              </div>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Size</label>
              <select
                value={filters.size}
                onChange={(e) => setFilters({...filters, size: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Sizes</option>
                {uniqueSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Origin</label>
              <select
                value={filters.origin}
                onChange={(e) => setFilters({...filters, origin: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Origins</option>
                {uniqueOrigins.map(origin => (
                  <option key={origin} value={origin}>{origin}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Type</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Types</option>
                {uniqueTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setFilters({startDate: '', endDate: '', size: '', origin: '', type: ''})}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Table header and search */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              Warehouse Calibration Records
            </h4>
            <p className="mt-1 text-sm text-gray-600">
              Track all raw cashew nut calibration records
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex h-10 items-center rounded-full bg-white px-4 dark:bg-navy-900">
              <span className="text-xl">
                <MdSearch className="h-5 w-5 text-gray-400" />
              </span>
              <input
                type="text"
                placeholder="Search..."
                className="ml-2 w-full bg-transparent text-sm font-medium text-gray-600 outline-none dark:text-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              onClick={handleExportCsv}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-green-700 active:bg-green-800"
              title="Export filtered records to CSV"
            >
              <MdFileDownload className="h-5 w-5" />
              Export CSV
            </button>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head, index) => (
                    <th
                      key={index}
                      className="border-b border-gray-200 pb-[10px] pr-4 text-start dark:!border-navy-700"
                    >
                      <p className="text-sm font-bold tracking-wide text-gray-800 dark:text-white">
                        {head}
                      </p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => (
                  <tr key={item.id}>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.id}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.rcn_type}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.origin}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.size}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.real} kg
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.number_of_bags}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.outturn}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.moisture}%
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.zone}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.agent}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/admin/warehouse/calibrated/${item.id}`)}
                          className="rounded-lg bg-slate-50 p-2 text-slate-600 hover:bg-slate-100 dark:bg-navy-700 dark:text-slate-300 dark:hover:bg-navy-600"
                          title="View"
                        >
                          <MdVisibility className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDownloadQr(item)}
                          className="rounded-lg bg-slate-50 p-2 text-slate-600 hover:bg-slate-100 dark:bg-navy-700 dark:text-slate-300 dark:hover:bg-navy-600"
                          title="Download QR"
                          disabled={downloadingQrId === item.id}
                        >
                          {downloadingQrId === item.id ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          ) : (
                            <MdQrCode2 className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDownloadRecordCard(item)}
                          className="flex min-w-[90px] items-center justify-center gap-1 rounded-lg bg-brand-500 px-3 py-2 text-xs font-bold text-white shadow-sm hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                          title="Download PDF"
                          disabled={downloadingCardId === item.id || printingCardId === item.id}
                        >
                          {downloadingCardId === item.id ? (
                            <>
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                              Downloading
                            </>
                          ) : (
                            <>
                              <MdDownload className="h-4 w-4" />
                              PDF
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleDownloadRecordCard(item, true)}
                          className="flex min-w-[98px] items-center justify-center gap-2 rounded-lg border-2 border-black bg-yellow-400 px-3 py-2 text-sm font-extrabold tracking-wide text-black shadow-md hover:bg-yellow-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white dark:bg-yellow-300 dark:text-black dark:hover:bg-yellow-200"
                          title="Print PDF"
                          disabled={downloadingCardId === item.id || printingCardId === item.id}
                        >
                          {printingCardId === item.id ? (
                            <>
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                              Opening
                            </>
                          ) : (
                            <>
                              <MdPrint className="h-5 w-5" />
                              PRINT
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="rounded-lg bg-red-50 p-2 text-red-600 hover:bg-red-100 dark:bg-navy-700 dark:text-red-400 dark:hover:bg-navy-600"
                        >
                          <MdDelete className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm font-medium text-navy-700 dark:text-white">
            Showing {paginatedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Previous
            </button>
            
            {/* Page number buttons */}
            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              // Calculate which page numbers to show
              let pageNum;
              if (totalPages <= 5) {
                pageNum = index + 1;
              } else if (currentPage <= 3) {
                pageNum = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + index;
              } else {
                pageNum = currentPage - 2 + index;
              }
              
              return (
                <button
                  key={index}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                    currentPage === pageNum
                      ? "bg-brand-700 text-white"
                      : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Last
            </button>
          </div>
        </div>

        {/* Add DeleteConfirmationModal */}
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setRecordToDelete(null);
          }}
          onConfirm={confirmDelete}
          recordId={recordToDelete}
          isDeleting={isDeleting}
        />
      </Card>
    </div>
  );
};

export default CalibrationRecords; 